// src/pages/PMTemplatePicker.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Main from "./Main";
import { makeCompanyNav } from "./nav";
import "./css/pm-template-picker.css";

import { API_BASE, getCsrfToken } from "../services/server";

/* ------------------------ inline api helpers ------------------------ */

async function apiFetch(path, { method = "GET", body } = {}) {
  const csrf = getCsrfToken();
  const url = `${API_BASE}${path}`;

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(csrf ? { "X-CSRFToken": csrf } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("text/html")) {
    const text = await res.text().catch(() => "");
    throw new Error(`Expected JSON but got HTML (${res.status}). ${text.slice(0, 200)}`);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Replace this path to whatever your backend exposes for templates.
 * Expected response: array or {results:[...]}
 */
async function fetchIMTemplates(orgnr) {
  return apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/templates/`);
}

/* ------------------------ UI ------------------------ */

const PREBUILT_TEMPLATE_NAMES = ["Standardlayout", "Kompakt", "Visuell"];

export default function PMTemplatePicker() {
  const { company: slug } = useParams();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        const resp = await fetchIMTemplates(slug);

        const list = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.results)
          ? resp.results
          : [];

        if (alive) setTemplates(list);
      } catch (e) {
        console.warn("Could not load IM templates:", e);
        if (alive) setTemplates([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [slug]);

  /* ------------------------ Split templates ------------------------ */

  // Global templates: is_global=true OR has a prebuilt name
  const prebuiltTemplates = templates.filter(
    (tpl) => tpl.is_global || PREBUILT_TEMPLATE_NAMES.includes(tpl.name)
  );

  // Workspace-scoped templates saved by the broker for this specific customer
  const userTemplates = templates.filter(
    (tpl) => !tpl.is_global && !PREBUILT_TEMPLATE_NAMES.includes(tpl.name)
  );

  /* ------------------------ Routing ------------------------ */

  const handlePickBlank = () => {
    navigate(`/company/${slug}/pm/editor`, {
      state: {
        template: null,
        templateId: null,
      },
    });
  };

  const handlePickTemplate = (tpl) => {
    navigate(`/company/${slug}/pm/editor`, {
      state: {
        template: tpl,
        templateId: tpl.id,
      },
    });
  };

  const getPrebuiltVariantClass = (tplName) => {
    if (tplName === "Standardlayout") return "standard";
    if (tplName === "Kompakt") return "compact";
    if (tplName === "Visuell") return "visual";
    return "user";
  };

  return (
    <Main navItems={makeCompanyNav(slug)}>
      <div className="pm-tp-page">
        {/* Header */}
        <div className="pm-tp-header">
          <h1>Mallar</h1>
          <p>
            Välj en IM-mall att utgå ifrån – antingen en tom layout, era egna
            sparade mallar eller våra fördefinierade grundmallar.
          </p>
        </div>

        <div className="pm-tp-content">
          {/* New / blank */}
          <section className="pm-tp-section">
            <h2>Ny</h2>

            <div className="pm-tp-grid">
              <div
                className="pm-tp-card pm-tp-card-empty"
                onClick={handlePickBlank}
              >
                <div className="pm-tp-card-inner pm-tp-card-inner--empty">
                  <div className="pm-tp-plus">＋</div>
                  <div className="pm-tp-card-label">Starta från blank IM</div>
                  <div className="pm-tp-card-sub">
                    Bygg layouten helt från grunden.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Divider */}
          <div className="pm-tp-divider">Era sparade mallar</div>

          {/* User saved templates */}
          <section className="pm-tp-section">
            {loading ? (
              <div className="pm-tp-loading">Laddar mallar…</div>
            ) : userTemplates.length === 0 ? (
              <div className="pm-tp-grid">
                <div className="pm-tp-card pm-tp-card-placeholder">
                  <div className="pm-tp-card-label">
                    Inga egna mallar sparade ännu.
                  </div>
                  <div className="pm-tp-card-sub">
                    Skapa en layout i editorn och spara den som mall.
                  </div>
                </div>
              </div>
            ) : (
              <div className="pm-tp-grid">
                {userTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="pm-tp-card pm-tp-card-template"
                    onClick={() => handlePickTemplate(tpl)}
                  >
                    <div className="pm-tp-card-inner">
                      <div className="pm-tp-thumb pm-tp-thumb--user" />
                    </div>
                    <div className="pm-tp-card-label">{tpl.name}</div>
                    <div className="pm-tp-card-sub">
                      {tpl.is_default ? "Standard för kunden" : "Egen mall"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Divider for prebuilt templates */}
          <div className="pm-tp-divider">Inbyggda &amp; företagsmallar</div>

          {/* Prebuilt templates (Standardlayout, Kompakt, Visuell) */}
          <section className="pm-tp-section">
            {loading ? (
              <div className="pm-tp-loading">Laddar inbyggda mallar…</div>
            ) : prebuiltTemplates.length === 0 ? (
              <div className="pm-tp-grid">
                <div className="pm-tp-card pm-tp-card-placeholder">
                  Inga inbyggda mallar hittades. (Kontrollera backend-seed.)
                </div>
              </div>
            ) : (
              <div className="pm-tp-grid">
                {prebuiltTemplates.map((tpl) => {
                  const variant = getPrebuiltVariantClass(tpl.name);
                  return (
                    <div
                      key={tpl.id}
                      className={`pm-tp-card pm-tp-card-template pm-tp-card-template--prebuilt pm-tp-card-template--${variant}`}
                      onClick={() => handlePickTemplate(tpl)}
                    >
                      <div className="pm-tp-card-inner">
                        <div className={`pm-tp-thumb pm-tp-thumb--${variant}`} />
                      </div>
                      <div className="pm-tp-card-label">{tpl.name}</div>
                      <div className="pm-tp-card-sub">
                        {tpl.name === "Standardlayout" &&
                          "Klassisk 1-kolumnslayout – bra bas."}
                        {tpl.name === "Kompakt" &&
                          "Mer komprimerad layout för kortare IM."}
                        {tpl.name === "Visuell" &&
                          "Mer visuellt fokus med fler bildytor."}
                        {!PREBUILT_TEMPLATE_NAMES.includes(tpl.name) &&
                          "Sparad företagsmall"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </Main>
  );
}